import { useEffect, useState } from "react";
import { api, errMsg, openPdf } from "../lib/api";
import { fmtMoney, fmtDateTime, Badge } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";

const EMPTY = { type: "particulier", name: "", company: "", phone: "", whatsapp: "", address: "", tax_id: "", credit_limit: 0, price_level: "detail" };

export default function Customers() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Client modifié" : "Client créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/customers/${confirmDel.id}`);
      toast.success("Client supprimé");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const showDetail = async (c) => {
    try {
      const { data } = await api.get(`/customers/${c.id}`);
      setDetail(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", label: "Client", render: (c) => (
      <div><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground">{c.company || c.type}{c.price_level !== "detail" ? ` · ${c.price_level}` : ""}</div></div>) },
    { key: "phone", label: "Téléphone" },
    { key: "total_purchases", label: "Total achats", className: "text-right", render: (c) => <span className="font-money">{fmtMoney(c.total_purchases, cur)}</span> },
    { key: "balance", label: "Solde", className: "text-right", render: (c) => (
      c.balance > 0.01
        ? <span className="font-money font-bold text-rose-600">Nous doit {fmtMoney(c.balance, cur)}</span>
        : c.balance < -0.01
          ? <span className="font-money font-bold text-blue-600">On lui doit {fmtMoney(-c.balance, cur)}</span>
          : <span className="font-money text-emerald-600">Soldé</span>) },
    { key: "actions", label: "", className: "text-right", render: (c) => (
      <div className="flex justify-end gap-1">
        <button data-testid={`view-customer-${c.id}`} onClick={() => showDetail(c)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Eye className="h-4 w-4" /></button>
        {hasPerm("customers.edit") && <button data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Pencil className="h-4 w-4" /></button>}
        {hasPerm("customers.delete") && <button data-testid={`delete-customer-${c.id}`} onClick={() => setConfirmDel(c)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Clients</h1>
        {hasPerm("customers.create") && <button data-testid="add-customer-btn" onClick={openNew} className={btnPrimary}><Plus className="h-4 w-4" /> Nouveau client</button>}
      </div>
      <DataTable testid="customers-table" columns={columns} rows={rows} searchKeys={["name", "company", "phone"]} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le client" : "Nouveau client"}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select data-testid="cust-type" value={form.type} onChange={set("type")} className={selectCls}>
              <option value="particulier">Particulier</option>
              <option value="entreprise">Entreprise</option>
            </select>
          </Field>
          <Field label="Tarification">
            <select data-testid="cust-price-level" value={form.price_level} onChange={set("price_level")} className={selectCls}>
              <option value="detail">Détail</option>
              <option value="grossiste">Grossiste</option>
              <option value="revendeur">Revendeur</option>
            </select>
          </Field>
        </div>
        <div className="space-y-3 mt-3">
          <Field label="Nom *"><input data-testid="cust-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <Field label="Société"><input data-testid="cust-company" value={form.company} onChange={set("company")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone"><input data-testid="cust-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="WhatsApp"><input data-testid="cust-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} /></Field>
          </div>
          <Field label="Adresse"><input data-testid="cust-address" value={form.address} onChange={set("address")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="N° fiscal"><input data-testid="cust-tax" value={form.tax_id} onChange={set("tax_id")} className={inputCls} /></Field>
            <Field label="Limite de crédit"><input data-testid="cust-credit-limit" type="number" min="0" value={form.credit_limit} onChange={set("credit_limit")} className={inputCls} /></Field>
          </div>
          <button data-testid="save-customer" onClick={save} disabled={!form.name} className={`${btnPrimary} w-full`}>{editing ? "Enregistrer" : "Créer"}</button>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name} wide>
        {detail && (
          <div className="space-y-4" data-testid="customer-detail">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-muted rounded-lg p-3"><div className="text-xs text-muted-foreground">Total achats</div><div className="font-money font-bold">{fmtMoney(detail.total_purchases, cur)}</div></div>
              <div className="bg-muted rounded-lg p-3"><div className="text-xs text-muted-foreground">Solde</div>
                <div className={`font-money font-bold ${detail.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {detail.balance > 0.01 ? `Doit ${fmtMoney(detail.balance, cur)}` : detail.balance < -0.01 ? `Crédit ${fmtMoney(-detail.balance, cur)}` : "Soldé"}
                </div></div>
              <div className="bg-muted rounded-lg p-3"><div className="text-xs text-muted-foreground">Limite crédit</div><div className="font-money font-bold">{fmtMoney(detail.credit_limit, cur)}</div></div>
            </div>
            <h3 className="font-heading font-bold">Historique des achats</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {detail.sales.length === 0 && <p className="text-sm text-muted-foreground">Aucun achat.</p>}
              {detail.sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm border rounded-lg p-2">
                  <div>
                    <button onClick={() => openPdf(`/sales/${s.id}/pdf`)} className="font-money font-bold text-primary hover:underline">{s.number}</button>
                    <span className="text-xs text-muted-foreground ml-2">{fmtDateTime(s.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2"><Badge status={s.payment_status} /><span className="font-money font-bold">{fmtMoney(s.total, cur)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer le client">
        <p className="text-sm">Supprimer <b>{confirmDel?.name}</b> ?</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-customer" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
